import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import Link from '../Link';
import Img from '../Img';
import Box from '../../shared/Box';
import { ButtonWithHoverAndPress } from '../../shared/Button';
import { AccountNavItems } from './components/AccountNavItems';
import { SignUpLoginNavItems } from './components/SignUpLoginNavItems';
import { HamburgerIcon } from './components/HamburgerIcon';
import { MobileNavContainer } from './components/MobileNav/MobileNavContainer';
import { NavLinks } from './components/NavLinks';
import { clickNavLink, clickedNavLinkGoogleAnalytics } from './events';

import { SIGNED_IN_LINKS, SIGNED_OUT_LINKS } from './constants';

import styles from './styles.sass';
import { AnchorAPI } from '../../modules/AnchorAPI';
import AnchorAPIError from '../../modules/AnchorAPI/AnchorAPIError';
import { getCurrentUser } from '../../user';

const cx = classnames.bind(styles);

const getIsACampaignAwaitingRecording = (campaigns = []) =>
  campaigns.some(campaign => campaign.campaignStatus === 'awaitingRecording');

const EPISODE_EDITOR_REGEX = /^\/dashboard\/episode\/([^\/]+\/edit)?$/;
const isShowingNewEpisodeButton = currentPath =>
  currentPath !== '/dashboard/episode/new' &&
  !currentPath.match(EPISODE_EDITOR_REGEX);

const getNavLinks = ({ user, campaigns, isAllowedToSeeMoneyNavItem }) => {
  if (user) {
    return SIGNED_IN_LINKS.reduce((navLinks, link) => {
      if (link.key === 'money') {
        return isAllowedToSeeMoneyNavItem
          ? [
              ...navLinks,
              {
                ...link,
                isShowingNotificationBadge: getIsACampaignAwaitingRecording(
                  campaigns
                ),
              },
            ]
          : navLinks;
      }
      return [...navLinks, link];
    }, []);
  }
  return SIGNED_OUT_LINKS;
};

const PublicWebsiteNav = ({
  campaigns,
  isAllowedToSeeMoneyNavItem,
  onLogOut,
  user,
  shareLinkPath,
  podcastNetwork,
  onClickNavItem,
  pathname,
  isMobileNavVisible,
  toggleMobileWebsiteNavVisible,
  podcastName,
  podcastImage,
}) => {
  const navLinks = getNavLinks({ user, campaigns, isAllowedToSeeMoneyNavItem });
  const [podcastEpisodes, setPodcastEpisodes] = useState([]);
  useEffect(() => {
    if (user) {
      getCurrentUser().then(res =>
        AnchorAPI.fetchPodcastProfileForStation(res.user.stationId)
          .then(({ episodes }) => {
            setPodcastEpisodes(episodes);
          })
          .catch(err => {
            throw new AnchorAPIError(
              err,
              'fetchPodcastProfileForStation in PublicWebsiteNav failed'
            );
          })
      );
    }
  }, [user]);
  return (
    <nav className={cx({ wrapper: true, sticky: pathname === '/' })}>
      <div
        className={cx({
          contentBackground: true,
        })}
      >
        <div className={styles.contentWrapper}>
          <Link
            href="/"
            onClick={() => {
              clickedNavLinkGoogleAnalytics('Home');
              clickNavLink({ link: 'icon' });
            }}
          >
            <Img
              className={styles.logoImage}
              src="https://d12xoj7p9moygp.cloudfront.net/images/anchor-logo-header.png"
              withRetina
              alt="Anchor logo"
              height={33}
              width={132}
            />
          </Link>
          <div className={styles.mainNav}>
            <div className={styles.desktopNav}>
              <div className={styles.leftContent}>
                <NavLinks links={navLinks} pathname={pathname} />
              </div>
              {user ? (
                <Box display="flex" direction="row">
                  {isShowingNewEpisodeButton(pathname) && (
                    <Link
                      to="/dashboard/episode/new"
                      onClick={() => clickNavLink({ link: 'new_episode' })}
                    >
                      <Box
                        width={180}
                        marginRight={26}
                        display="flex"
                        direction="row"
                        alignItems="center"
                      >
                        <ButtonWithHoverAndPress
                          isFullWidth
                          text="New Episode"
                          type="outlined"
                          shape="pill"
                          colorTheme="white"
                          size="sm"
                          isEventBubblingAllowed
                        />
                      </Box>
                    </Link>
                  )}
                  <AccountNavItems
                    shareLinkPath={shareLinkPath}
                    onClickNavItem={onClickNavItem}
                    podcastNetwork={podcastNetwork}
                    onLogOut={onLogOut}
                    podcastName={podcastName}
                    podcastImage={podcastImage}
                    isShowingDistribution={Boolean(podcastEpisodes.length)}
                  />
                </Box>
              ) : (
                <SignUpLoginNavItems pathname={pathname} />
              )}
            </div>
            <div className={styles.mobileNav}>
              <HamburgerIcon onClick={toggleMobileWebsiteNavVisible} />
              <MobileNavContainer
                pathname={pathname}
                links={navLinks}
                isSignedIn={!!user}
                onClickClose={toggleMobileWebsiteNavVisible}
                isVisible={isMobileNavVisible}
                onLogOut={onLogOut}
                isShowingDistribution={Boolean(podcastEpisodes.length)}
              />
              )
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

PublicWebsiteNav.propTypes = {
  onLogOut: PropTypes.func.isRequired,
  user: PropTypes.shape({
    imageUrl: PropTypes.string,
    userId: PropTypes.number,
    email: PropTypes.string,
    name: PropTypes.string,
  }),
  shareLinkPath: PropTypes.string,
  podcastNetwork: PropTypes.bool,
  onClickNavItem: PropTypes.func.isRequired,
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      campaignStatus: PropTypes.string,
    })
  ),
  pathname: PropTypes.string,
  isMobileNavVisible: PropTypes.bool,
  isAllowedToSeeMoneyNavItem: PropTypes.bool,
  toggleMobileWebsiteNavVisible: PropTypes.func.isRequired,
  podcastName: PropTypes.string,
  podcastImage: PropTypes.string,
};

PublicWebsiteNav.defaultProps = {
  podcastNetwork: null,
  shareLinkPath: null,
  user: null,
  campaigns: [],
  pathname: null,
  isMobileNavVisible: false,
  isAllowedToSeeMoneyNavItem: false,
  podcastName: null,
  podcastImage: null,
};

export { PublicWebsiteNav as default, PublicWebsiteNav };
